import * as pbi from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
import React, {
  createContext,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import { IEmbedReport } from 'types'

interface IReportsContext {
  reportLoaded: boolean
  setReportLoaded: React.Dispatch<SetStateAction<boolean>> 
  embedReport: pbi.Report | null
  setEmbedReport: React.Dispatch<SetStateAction<pbi.Report | null>>
  report: {name: string | null, slug: string | null} | IEmbedReport | null
  setReport: React.Dispatch<SetStateAction<{name: string | null, slug: string | null} | IEmbedReport |null>>
  reportPages: pbi.Page[] | []
  setReportPages: React.Dispatch<SetStateAction<pbi.Page[]>>
  getReportPages: (report: any) => void
  clearReport: () => void
}

const ReportsContext = createContext<IReportsContext>({
  reportLoaded: false,
  setReportLoaded: () => {},
  embedReport: null,
  setEmbedReport: () => {},
  report: null,
  setReport: () => {},
  reportPages: [],
  setReportPages: () => {},
  getReportPages: () => {},
  clearReport: () => {},
})

export function ReportsProvider({ children }) {
  const [reportLoaded, setReportLoaded] = useState(false)
  const [embedReport, setEmbedReport] = useState<pbi.Report | null>(null)
  const [reportPages, setReportPages] = useState<pbi.Page[]>([])
  const [report, setReport] = useState<{name: string | null, slug: string | null} | IEmbedReport | null>(null)

  const history = useHistory()

  const getReportPages = async (report: pbi.Report) => {
    const pages = await report.getPages()
    // filter's by page visibility
    return pages.filter((page) => page.visibility !== 1)
  }

  const clearReport = () => {
    setEmbedReport(null)
    setReportPages([])
    setReport(null)
  }

  useEffect(() => {
    const unlisten = history.listen((loc) => {
      if (!loc.pathname.includes('dashboard')) {
        clearReport()
        setReportLoaded(false)
        setEmbedReport(null)
      }
    })
    return unlisten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setReportPages([])
    // Strip leading dash from report name (e.g. "-Maya" → "Maya")
    if (report && !(report instanceof PowerBIEmbed) && report?.name?.startsWith('-')) {
      setReport((prev) => prev ? { ...prev, name: prev.name?.slice(1) ?? null } : null)
    }
  }, [report])

  return (
    <ReportsContext.Provider
      value={{
        reportLoaded,
        setReportLoaded,
        embedReport,
        setEmbedReport,
        report,
        setReport,
        reportPages,
        setReportPages,
        getReportPages,
        clearReport,
      }}
    >
      {children}
    </ReportsContext.Provider>
  )
}

export const useReportsCtx = () => {
  return { ...useContext(ReportsContext) }
}
